import { LogLevel, getWebInstrumentations, initializeFaro, type Faro } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

type FaroLogAttributes = Record<string, unknown>;

const FARO_APP_NAME = 'Create Mail Out';
const FARO_APP_VERSION = '1.0.0';
const LOCAL_ENVIRONMENTS = ['local', 'development', 'dev', 'test'];
const DEFAULT_PRODUCTION_SAMPLE_RATE = 0.2;
const DEFAULT_FARO_COLLECTOR_URL = 'https://faro-collector-prod-eu-west-3.grafana.net/collect';

let faroInstance: Faro | null = null;
let isFaroInitialized = false;

function stringifyLogAttributes(
  attributes?: FaroLogAttributes
): Record<string, string> | undefined {
  if (!attributes) {
    return undefined;
  }

  return Object.entries(attributes).reduce<Record<string, string>>(
    (contextEntries, [key, value]) => {
      if (value === undefined || value === null) {
        return contextEntries;
      }
      contextEntries[key] = typeof value === 'object' ? JSON.stringify(value) : String(value);
      return contextEntries;
    },
    {}
  );
}

function getFaroEnvironment(): string {
  return String(
    import.meta.env.VITE_FARO_ENVIRONMENT || import.meta.env.VITE_APP_ENV || 'development'
  );
}

function getProductionSamplingRate(): number {
  const configuredRate = Number.parseFloat(
    String(import.meta.env.VITE_FARO_SESSION_SAMPLE_RATE_PROD || '')
  );
  if (Number.isFinite(configuredRate) && configuredRate > 0 && configuredRate <= 1) {
    return configuredRate;
  }

  return DEFAULT_PRODUCTION_SAMPLE_RATE;
}

function getSessionSamplingRate(environmentName: string): number {
  if (LOCAL_ENVIRONMENTS.includes(environmentName.toLowerCase())) {
    return 1;
  }

  return getProductionSamplingRate();
}

function buildCollectorUrl(): string {
  const fullUrl = String(import.meta.env.VITE_FARO_COLLECTOR_URL || '').trim();
  if (fullUrl) {
    return fullUrl;
  }

  const collectorId = String(import.meta.env.VITE_FARO_COLLECTOR || '').trim();
  if (collectorId) {
    return `${DEFAULT_FARO_COLLECTOR_URL}/${collectorId}`;
  }

  return '';
}

export function initFaro(): void {
  if (isFaroInitialized) {
    return;
  }

  const collectorUrl = buildCollectorUrl();
  const collectorToken = String(import.meta.env.VITE_FARO_COLLECTOR_TOKEN || '').trim();

  if (!collectorUrl) {
    return;
  }

  const environmentName = getFaroEnvironment();

  const faroConfig: Parameters<typeof initializeFaro>[0] = {
    url: collectorUrl,
    app: {
      name: FARO_APP_NAME,
      version: FARO_APP_VERSION,
      environment: environmentName,
    },
    sessionTracking: {
      samplingRate: getSessionSamplingRate(environmentName),
    },
    batching: {
      sendTimeout: 3000,
    },
    instrumentations: LOCAL_ENVIRONMENTS.includes(environmentName.toLowerCase())
      ? [...getWebInstrumentations()]
      : [...getWebInstrumentations(), new TracingInstrumentation()],
  };

  if (collectorToken) {
    faroConfig.apiKey = collectorToken;
  }

  faroInstance = initializeFaro(faroConfig);
  isFaroInitialized = true;
}

export function pushFaroLog(message: string, attributes?: FaroLogAttributes): void {
  if (!faroInstance) {
    return;
  }

  faroInstance.api.pushLog([message], {
    level: LogLevel.INFO,
    context: stringifyLogAttributes(attributes),
  });
}

export function pushFaroLogError(message: string, attributes?: FaroLogAttributes): void {
  if (!faroInstance) {
    return;
  }

  faroInstance.api.pushLog([message], {
    level: LogLevel.ERROR,
    context: stringifyLogAttributes(attributes),
  });
  faroInstance.api.pushError(new Error(message), {
    context: stringifyLogAttributes(attributes),
  });
}
